<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="800px" persistent>
    <v-card>
      <v-card-title class="warning white--text">
        <v-icon left>mdi-clock-plus</v-icon>
        {{ editMode ? 'แก้ไข' : 'เพิ่ม' }}ข้อมูลการขาดลามาสาย
      </v-card-title>
      
      <v-card-text class="pt-4">
        <v-form ref="attendanceForm" v-model="formValid">
          <v-row>
            <!-- วันที่ -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="localData.attendance_date"
                label="วันที่"
                type="date"
                outlined
                :rules="[v => !!v || 'กรุณาระบุวันที่']"
                :max="maxDate"
                hint="เลือกวันที่เกิดเหตุการณ์"
                persistent-hint
              />
            </v-col>
            
            <!-- ประเภท -->
            <v-col cols="12" md="6">
              <v-select
                v-model="localData.attendance_type"
                :items="attendanceTypes"
                label="ประเภท"
                outlined
                :rules="[v => !!v || 'กรุณาเลือกประเภท']"
                @change="onTypeChange"
              />
            </v-col>
            
            <!-- เวลาที่กำหนด (แสดงเมื่อไม่ใช่ขาด) -->
            <v-col cols="12" md="6" v-if="localData.attendance_type !== 'absent'">
              <v-text-field
                v-model="localData.start_time"
                label="เวลาที่กำหนด"
                type="time"
                outlined
                :rules="timeRules"
                hint="เวลาที่ควรจะมาทำงาน/กลับ"
                persistent-hint
              />
            </v-col>
            
            <!-- เวลาที่มาจริง/กลับจริง -->
            <v-col cols="12" md="6" v-if="localData.attendance_type !== 'absent'">
              <v-text-field
                v-model="localData.actual_time"
                :label="actualTimeLabel"
                type="time"
                outlined
                :rules="timeRules"
                @change="calculateDuration"
                :hint="actualTimeHint"
                persistent-hint
              />
            </v-col>
            
            <!-- ระยะเวลา (นาที) - คำนวณอัตโนมัติ -->
            <v-col cols="12" md="6" v-if="localData.attendance_type !== 'absent'">
              <v-text-field
                v-model="localData.duration_minutes"
                label="ระยะเวลา (นาที)"
                type="number"
                min="0"
                outlined
                :rules="[v => v >= 0 || 'ระยะเวลาต้องไม่น้อยกว่า 0']"
                readonly
                :color="durationColor"
                :hint="durationHint"
                persistent-hint
              >
                <template v-slot:append>
                  <v-icon :color="durationColor">{{ durationIcon }}</v-icon>
                </template>
              </v-text-field>
            </v-col>
            
            <!-- สถานะการอนุมัติ -->
            <v-col cols="12" md="6">
              <v-select
                v-model="localData.status"
                :items="statusOptions"
                label="สถานะการอนุมัติ"
                outlined
                :rules="[v => !!v || 'กรุณาเลือกสถานะ']"
              />
            </v-col>
            
            <!-- ผู้อนุมัติ (แสดงเมื่อสถานะเป็น approved) -->
            <v-col cols="12" md="6" v-if="localData.status === 'approved'">
              <v-text-field
                v-model="localData.approved_by"
                label="ผู้อนุมัติ"
                outlined
                :rules="[v => !!v || 'กรุณาระบุผู้อนุมัติ']"
                prepend-inner-icon="mdi-account-check"
              />
            </v-col>
            
            <!-- เหตุผล -->
            <v-col cols="12">
              <v-textarea
                v-model="localData.reason"
                label="เหตุผล"
                outlined
                rows="3"
                counter="500"
                :rules="reasonRules"
                hint="อธิบายสาเหตุของการขาด/สาย/กลับก่อน"
                persistent-hint
              />
            </v-col>
            
            <!-- หมายเหตุ -->
            <v-col cols="12">
              <v-textarea
                v-model="localData.note"
                label="หมายเหตุ (ไม่บังคับ)"
                outlined
                rows="2"
                counter="300"
                :rules="[v => !v || v.length <= 300 || 'หมายเหตุต้องไม่เกิน 300 ตัวอักษร']"
                hint="ข้อมูลเพิ่มเติมหรือหมายเหตุประกอบ"
                persistent-hint
              />
            </v-col>
            
            <!-- อัพโหลดไฟล์หลักฐาน -->
            <v-col cols="12">
              <v-file-input
                v-model="localData.file"
                label="ไฟล์หลักฐาน (ไม่บังคับ)"
                accept="image/*,.pdf"
                outlined
                show-size
                prepend-icon="mdi-paperclip"
                :rules="fileRules"
                hint="อัพโหลดหลักฐานประกอบ เช่น ใบรับรองแพทย์"
                persistent-hint
              >
                <template v-slot:selection="{ text }">
                  <v-chip small label color="warning">
                    <v-icon left small>mdi-file</v-icon>
                    {{ text }}
                  </v-chip>
                </template>
              </v-file-input>
            </v-col>
          </v-row>
          
          <!-- แสดงคำเตือนและข้อมูลสำคัญ -->
          <div class="mt-4">
            <!-- คำเตือนเมื่อข้อมูลไม่สมเหตุสมผล -->
            <v-alert
              v-if="showWarning"
              :type="warningType"
              outlined
              dense
              class="mb-3"
            >
              <div class="font-weight-medium mb-1">{{ warningTitle }}</div>
              <div class="text-caption">{{ warningMessage }}</div>
            </v-alert>
            
            <!-- แสดงสรุปข้อมูล -->
            <v-card v-if="dataSummary" outlined>
              <v-card-subtitle class="pb-2">
                <v-icon left small>mdi-information</v-icon>
                สรุปข้อมูล
              </v-card-subtitle>
              <v-card-text class="pt-0">
                <v-row dense>
                  <v-col cols="6">
                    <div class="text-caption grey--text">วันที่:</div>
                    <div class="font-weight-medium">{{ formatDate(localData.attendance_date) }}</div>
                  </v-col>
                  <v-col cols="6">
                    <div class="text-caption grey--text">ประเภท:</div>
                    <v-chip small :color="getTypeColor(localData.attendance_type)">
                      {{ getTypeLabel(localData.attendance_type) }}
                    </v-chip>
                  </v-col>
                  <v-col cols="6" v-if="localData.duration_minutes > 0">
                    <div class="text-caption grey--text">ระยะเวลา:</div>
                    <div class="font-weight-medium">{{ formatDuration(localData.duration_minutes) }}</div>
                  </v-col>
                  <v-col cols="6">
                    <div class="text-caption grey--text">สถานะ:</div>
                    <v-chip small :color="getStatusColor(localData.status)">
                      {{ getStatusLabel(localData.status) }}
                    </v-chip>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>
        </v-form>
      </v-card-text>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('close')">ยกเลิก</v-btn>
        <v-btn text @click="resetForm" color="grey">
          <v-icon left small>mdi-refresh</v-icon>
          ล้างข้อมูล
        </v-btn>
        <v-btn 
          color="warning" 
          :loading="saving"
          :disabled="!formValid"
          @click="handleSave"
        >
          <v-icon left>mdi-content-save</v-icon>
          บันทึก
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'AttendanceDialog',
  
  props: {
    value: Boolean,
    editMode: Boolean,
    data: Object,
    saving: Boolean
  },
  
  data() {
    return {
      localData: {},
      formValid: false,
      
      attendanceTypes: [
        { text: 'มาสาย', value: 'late' },
        { text: 'ขาดงาน', value: 'absent' },
        { text: 'กลับก่อนเวลา', value: 'early_leave' }
      ],
      
      statusOptions: [
        { text: 'รอพิจารณา', value: 'pending' },
        { text: 'อนุมัติ', value: 'approved' },
        { text: 'ไม่อนุมัติ', value: 'rejected' }
      ],
      
      timeRules: [
        v => !!v || 'กรุณาระบุเวลา'
      ],
      
      reasonRules: [
        v => !!v || 'กรุณาระบุเหตุผล',
        v => v && v.trim().length >= 10 || 'เหตุผลต้องมีความยาวอย่างน้อย 10 ตัวอักษร',
        v => v && v.length <= 500 || 'เหตุผลต้องไม่เกิน 500 ตัวอักษร'
      ],
      
      fileRules: [
        v => !v || v.size < 5000000 || 'ไฟล์ต้องมีขนาดไม่เกิน 5MB'
      ]
    }
  },
  
  computed: {
    maxDate() {
      return new Date().toISOString().split('T')[0]
    },
    
    actualTimeLabel() {
      switch (this.localData.attendance_type) {
        case 'late':
          return 'เวลาที่มาทำงานจริง'
        case 'early_leave':
          return 'เวลาที่กลับจริง'
        default:
          return 'เวลาจริง'
      }
    },
    
    actualTimeHint() {
      switch (this.localData.attendance_type) {
        case 'late':
          return 'เวลาที่มาถึงที่ทำงานจริง'
        case 'early_leave':
          return 'เวลาที่ออกจากที่ทำงานจริง'
        default:
          return ''
      }
    },
    
    durationColor() {
      if (!this.localData.duration_minutes) return 'grey'
      if (this.localData.duration_minutes > 120) return 'red'
      if (this.localData.duration_minutes > 60) return 'orange'
      if (this.localData.duration_minutes > 30) return 'amber'
      return 'blue'
    },
    
    durationIcon() {
      if (!this.localData.duration_minutes) return 'mdi-timer-outline'
      if (this.localData.duration_minutes > 120) return 'mdi-alert-circle'
      if (this.localData.duration_minutes > 60) return 'mdi-clock-alert'
      return 'mdi-timer'
    },
    
    durationHint() {
      if (!this.localData.duration_minutes) return 'คำนวณอัตโนมัติจากเวลา'
      return `${this.formatDuration(this.localData.duration_minutes)}`
    },
    
    showWarning() {
      return this.warningMessage.length > 0
    },
    
    warningType() {
      if (this.localData.duration_minutes > 120) return 'error'
      if (this.localData.attendance_type === 'absent' && !this.localData.reason.trim()) return 'warning'
      if (this.localData.duration_minutes > 60) return 'warning'
      return 'info'
    },
    
    warningTitle() {
      if (this.localData.duration_minutes > 120) return 'ข้อมูลผิดปกติ'
      if (this.localData.attendance_type === 'absent' && !this.localData.reason.trim()) return 'กรุณาระบุเหตุผล'
      if (this.localData.duration_minutes > 60) return 'ระยะเวลานาน'
      return 'ข้อมูล'
    },
    
    warningMessage() {
      let messages = []
      
      if (this.localData.attendance_type === 'absent' && !this.localData.reason.trim()) {
        messages.push('การขาดงานควรระบุเหตุผลที่ชัดเจนและครบถ้วน')
      }
      
      if (this.localData.duration_minutes > 120) {
        messages.push('ระยะเวลาเกิน 2 ชั่วโมง กรุณาตรวจสอบความถูกต้องของเวลา')
      } else if (this.localData.duration_minutes > 60) {
        messages.push('ระยะเวลาเกิน 1 ชั่วโมง อาจต้องการการอนุมัติพิเศษ')
      }
      
      if (this.localData.attendance_type === 'late' && this.localData.actual_time && this.localData.start_time) {
        const actualTime = this.parseTime(this.localData.actual_time)
        const startTime = this.parseTime(this.localData.start_time)
        if (actualTime <= startTime) {
          messages.push('เวลาที่มาจริงควรหลังจากเวลาที่กำหนด')
        }
      }
      
      return messages.join(' • ')
    },
    
    dataSummary() {
      return this.localData.attendance_date && this.localData.attendance_type && this.localData.reason
    }
  },
  
  watch: {
    data: {
      handler(newData) {
        this.localData = { ...newData }
      },
      immediate: true,
      deep: true
    },
    
    value(newVal) {
      if (newVal) {
        this.localData = { ...this.data }
        this.$nextTick(() => {
          if (this.$refs.attendanceForm) {
            this.$refs.attendanceForm.resetValidation()
          }
        })
      }
    }
  },
  
  methods: {
    onTypeChange() {
      // ล้างค่าเวลาเมื่อเปลี่ยนประเภท
      if (this.localData.attendance_type === 'absent') {
        this.localData.start_time = ''
        this.localData.actual_time = ''
        this.localData.duration_minutes = 0
      } else {
        // ตั้งค่าเริ่มต้นตามประเภท
        if (!this.localData.start_time) {
          if (this.localData.attendance_type === 'late') {
            this.localData.start_time = '08:30'
          } else if (this.localData.attendance_type === 'early_leave') {
            this.localData.start_time = '16:30'
          }
        }
      }
    },
    
    calculateDuration() {
      if (!this.localData.start_time || !this.localData.actual_time) {
        this.localData.duration_minutes = 0
        return
      }
      
      const startTime = this.parseTime(this.localData.start_time)
      const actualTime = this.parseTime(this.localData.actual_time)
      
      let diffMinutes = 0
      
      if (this.localData.attendance_type === 'late') {
        // คำนวณความสายจากเวลาที่กำหนด
        if (actualTime > startTime) {
          diffMinutes = actualTime - startTime
        }
      } else if (this.localData.attendance_type === 'early_leave') {
        // คำนวณเวลาที่กลับก่อน
        if (actualTime < startTime) {
          diffMinutes = startTime - actualTime
        }
      }
      
      this.localData.duration_minutes = Math.max(0, diffMinutes)
    },
    
    parseTime(timeStr) {
      const [hours, minutes] = timeStr.split(':').map(Number)
      return hours * 60 + minutes
    },
    
    resetForm() {
      this.localData = {
        id: null,
        attendance_date: '',
        attendance_type: 'late',
        start_time: '08:30',
        actual_time: '',
        duration_minutes: 0,
        reason: '',
        approved_by: '',
        status: 'pending',
        file: null,
        note: ''
      }
      if (this.$refs.attendanceForm) {
        this.$refs.attendanceForm.resetValidation()
      }
    },
    
    handleSave() {
      if (this.$refs.attendanceForm.validate()) {
        this.$emit('save', this.localData)
      }
    },
    
    // Helper methods for display
    getTypeColor(type) {
      switch (type) {
        case 'absent': return 'red'
        case 'late': return 'orange'
        case 'early_leave': return 'primary'
        default: return 'grey'
      }
    },
    
    getTypeLabel(type) {
      switch (type) {
        case 'absent': return 'ขาดงาน'
        case 'late': return 'มาสาย'
        case 'early_leave': return 'กลับก่อน'
        default: return type
      }
    },
    
    getStatusColor(status) {
      switch (status) {
        case 'approved': return 'green'
        case 'rejected': return 'red'
        case 'pending': return 'orange'
        default: return 'grey'
      }
    },
    
    getStatusLabel(status) {
      switch (status) {
        case 'approved': return 'อนุมัติ'
        case 'rejected': return 'ไม่อนุมัติ'
        case 'pending': return 'รอพิจารณา'
        default: return status
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      try {
        return new Date(dateString).toLocaleDateString('th-TH')
      } catch {
        return dateString
      }
    },
    
    formatDuration(minutes) {
      if (!minutes) return '0 นาที'
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      
      if (hours > 0) {
        return `${hours} ชั่วโมง ${mins} นาที`
      }
      return `${mins} นาที`
    }
  }
}
</script>

<style scoped>
/* Custom styling for attendance dialog */
.v-card__subtitle {
  padding-bottom: 8px;
}

.v-alert .v-icon {
  align-self: flex-start;
}

/* Form field spacing */
.v-text-field .v-input__slot {
  min-height: 48px;
}

.v-textarea .v-input__slot {
  padding-top: 8px;
}

/* File input styling */
.v-file-input .v-file-input__text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Chip styling in summary */
.v-chip.v-size--small {
  height: 20px;
  font-size: 11px;
}

/* Duration field enhancement */
.v-input--is-readonly .v-input__append-inner {
  margin-top: 4px;
}

/* Alert spacing */
.v-alert + .v-alert {
  margin-top: 8px;
}
</style>